import {
  ICcyConversionOrdersQuery,
  IMexDolarDetails,
  IRouterPaginationResponse,
} from "interfaces";
import { institutionalApi } from "./index";

import {
  IRouterOrder,
  AdminSummaryResponse,
  AdminVolSumResponse,
  ShinkansenTransfer,
  DestinationAccount,
  BrokerAdminSummaryResponse,
  IClient,
  IBroker,
  IUserEdit,
  ICancellationLoss,
  IUserCreate,
  IClientCreate,
  IRouterCreateBankAcc,
  GetAdminDestinationAccounts,
  AllClientsDTO,
} from "interfaces/router";
import { buildUrlWithFilters, transformErrorResponse } from "utils";
import {
  FlagEnum,
  ICCYOptions,
  RouterSupportedCountry,
  RouterSupportedFiat,
  SettlementType,
} from "interfaces/enums";

export interface IQuoteStreamLeg {
  lp: string | null;
  quote_id: string | null;
  rate: string | null;
  qty_src: string | null;
  qty_dest: string | null;
  src: string | null;
  dest: string | null;
  ref: string | null; // near-real-time mid-market reference for the leg
}

export interface IQuoteStreamEntry {
  id: string;
  t: number | string; // quote time_created (epoch seconds or ISO datetime)
  d_fx: boolean;
  transfer_method: string | null;
  on_ramp: IQuoteStreamLeg;
  off_ramp: IQuoteStreamLeg;
  quote: {
    qty_src: string;
    qty_dest: string;
    src: string;
    dest: string;
    ref: string | null; // mid-market reference
    rate_ridian: string;
    rate_quote: string;
    rate_client: string;
    rate_feeless?: string | null; // pure FX rate, excludes transfer costs
  };
  trading_desk: Omit<IQuoteStreamLeg, "ref"> | null;
  fees: Array<{
    defer: boolean;
    fee: string | null;
  }>;
  user: {
    id: string;
    spread: string | null;
  };
  broker: {
    id: string | null;
    spread: string | null;
  };
}

export interface IQuoteStreamQuery {
  start_date?: string;
  end_date?: string;
  user_id?: string;
  limit?: number;
  cursor?: string;
}

export interface CreateOrderDTO {
  order_id: string;
  user_id: string;
  destination_account_id: string;
  consolidating_reference: string;
  broker_id: string;
  ccy_source: RouterSupportedFiat;
  ccy_dest: RouterSupportedFiat;
  ccy_of_qty: ICCYOptions;
  country_source: RouterSupportedCountry;
  country_dest: RouterSupportedCountry;
  transfer_method: string;
  qty_source: string;
  qty_dest: string;
  rate_ridian: string;
  rate_quote: string;
  rate_client: string;
  geolocation_lat: string;
  geolocation_long: string;
  settlement_type: SettlementType;
  defer_fees: boolean;
  memo: string;
  time_created?: string;
  ccy_fee_transfer: string;
  qty_fee_transfer: string;
}

export const adminApi = institutionalApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdminSummary: builder.query<
      {
        client_data: Array<AdminSummaryResponse>;
        broker_data: { [k: string]: BrokerAdminSummaryResponse };
        vol_sum: AdminVolSumResponse;
      },
      void
    >({
      query: (params) => ({
        url: `/router/admin/client_summary`,
        method: "GET",
      }),
      providesTags: [{ type: "AdminSummary" }],
      transformErrorResponse,
    }),
    userFlags: builder.query<Array<FlagEnum>, undefined>({
      query: () => ({
        url: "/router/admin/user/flags",
        method: "GET",
      }),
      transformResponse: (response: Array<FlagEnum>) => {
        return response.sort((a, b) => a.localeCompare(b)) as Array<FlagEnum>;
      },
      providesTags: ["Flags"],
    }),
    getLPDetails: builder.query<
      {
        on_ramp_lp: string;
        on_ramp_lp_id: string;
        on_ramp_order_status: string;
        off_ramp_lp: string;
        off_ramp_lp_id: string;
        off_ramp_order_status: string;
      },
      {
        order_id: string;
      }
    >({
      query: (params) => ({
        url: `/router/admin/orders/lp_details`,
        method: "GET",
        params,
      }),
      providesTags: (_, __, params) => [
        { type: "LPDetails", id: params.order_id },
      ],
      transformErrorResponse,
    }),
    getAdminOrders: builder.query<
      { orders: Array<IRouterOrder>; vol_daily: Array<[string, number]> },
      ICcyConversionOrdersQuery
    >({
      query: (params) => ({
        url: `/router/admin/orders`,
        method: "GET",
        params,
      }),
      providesTags: ["AdminOrders"],
      transformErrorResponse,
    }),
    getAllClients: builder.query<AllClientsDTO, void>({
      query: (params) => ({
        url: `/router/admin/clients/all`,
        method: "GET",
        params,
      }),
      providesTags: ["AllClients"],
      transformErrorResponse,
    }),
    createUserOnlyAdmin: builder.mutation<string, IUserCreate>({
      query: (body) => ({
        url: "/router/admin/user/user",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AllClients"],
      transformErrorResponse,
    }),
    createUserAdmin: builder.mutation<string, IClientCreate>({
      query: (body) => ({
        url: "/router/admin/user/account",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AllClients"],
      transformErrorResponse,
    }),
    approveOrder: builder.mutation<
      string,
      { order_router_id: string; approval: boolean }
    >({
      query: (body) => ({
        url: "/router/admin/under_review/approval",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminOrders"],
      transformErrorResponse,
    }),
    updateUser: builder.mutation<
      string,
      {
        user_id: string;
        flags: Array<FlagEnum>;
      }
    >({
      query: (body) => ({
        url: "/router/admin/user/flags",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AllClients"],
      transformErrorResponse,
    }),
    updateUserAdmin: builder.mutation<string, IUserEdit>({
      query: (body) => ({
        url: "/router/admin/user/modify",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AllClients"],
      transformErrorResponse,
    }),
    updateOrderStatus: builder.mutation<
      string,
      {
        order_router_id: string;
        status: string;
        queryArgs?: ICcyConversionOrdersQuery;
      }
    >({
      query: ({ queryArgs, ...body }) => ({
        url: "/router/admin/order/status",
        method: "POST",
        body,
      }),
      async onQueryStarted(
        { order_router_id, status, queryArgs },
        { dispatch, queryFulfilled },
      ) {
        if (!queryArgs) return;

        const patchResult = dispatch(
          adminApi.util.updateQueryData(
            "getAdminOrders",
            queryArgs,
            (draft) => {
              const order = draft.orders.find((o) => o.id === order_router_id);
              if (order) {
                order.status = status as any;
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: ["AdminOrders"],
      transformErrorResponse,
    }),
    createBrokerAdmin: builder.mutation<string, IBroker>({
      query: (body) => ({
        url: "/router/admin/user/broker",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AllClients"],
      transformErrorResponse,
    }),
    modifyBrokerUsers: builder.mutation<string, IClient>({
      query: (body) => ({
        url: "/router/admin/user/broker/modify",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AllClients"],
      transformErrorResponse,
    }),
    getAdminTransferAccounts: builder.query<Array<IRouterCreateBankAcc>, void>({
      query: () => ({
        url: `/shinkansen/admin/transfer/accounts`,
        method: "GET",
      }),
      providesTags: ["ShinkansenAccounts"],
      transformErrorResponse,
    }),
    addAdminTransferAccounts: builder.mutation<
      string,
      {
        account_name: string;
        bank_name: string;
        clabe: string;
        rfc: string;
      }
    >({
      query: (body) => ({
        url: `/shinkansen/admin/transfer/accounts`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ShinkansenAccounts"],
      transformErrorResponse,
    }),
    getShinkansenBalance: builder.query<{ qty: string; ccy: "MXN" }, void>({
      query: () => ({
        url: `/shinkansen/admin/balance`,
        method: "GET",
      }),
      providesTags: ["ShinkansenBalance"],
      transformErrorResponse,
    }),
    postShinkansenTransfer: builder.mutation<string, ShinkansenTransfer>({
      query: (body) => ({
        url: "/shinkansen/admin/transfer",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ShinkansenBalance"],
      transformErrorResponse,
    }),
    cancelationLoss: builder.mutation<string, ICancellationLoss>({
      query: (body) => ({
        url: "/router/admin/user/cancelation_loss",
        method: "POST",
        body,
      }),
      transformErrorResponse,
    }),
    getDestinationAccounts: builder.query<
      IRouterPaginationResponse<DestinationAccount>,
      GetAdminDestinationAccounts
    >({
      query: (params) => ({
        url: buildUrlWithFilters(`/router/admin/destination_accounts`, params),
      }),
      transformErrorResponse,
      providesTags: ["AdminDestinationAccounts"],
    }),
    updateDestinationAccountStatus: builder.mutation<
      string,
      { destination_account_id: string; status: string }
    >({
      query: (params) => ({
        url: "/router/admin/destination_accounts/status",
        method: "POST",
        body: params,
      }),
      invalidatesTags: ["AdminDestinationAccounts"],
      transformErrorResponse,
    }),
    deleteDestinationAccount: builder.mutation<
      string,
      { destination_account_id: string }
    >({
      query: (params) => ({
        url: "/router/admin/destination_account",
        method: "DELETE",
        params,
      }),
      invalidatesTags: ["AdminDestinationAccounts"],
      transformErrorResponse,
    }),
    confirmOutboundTransfer: builder.mutation<
      string,
      { order_id: string; destination_transfer_id: string }
    >({
      query: (body) => ({
        url: "/router/order/admin/confirm_outbound_transfer",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminOrders"],
      transformErrorResponse,
    }),
    confirmSPIDTransfer: builder.mutation<string, { order_id: string }>({
      query: (body) => ({
        url: "/router/admin/order/spid/confirm_transfer",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminOrders"],
      transformErrorResponse,
    }),
    resendWelcomeEmail: builder.mutation<string, { user_id: string }>({
      query: (params) => ({
        url: `/router/admin/resend_welcome_email/${params.user_id}`,
        method: "POST",
      }),
      transformErrorResponse,
    }),
    removeRollover: builder.mutation<string, { order_id: string }>({
      query: (body) => ({
        url: "/router/admin/order/remove_rollover",
        method: "POST",
        body,
      }),
      transformErrorResponse,
      invalidatesTags: ["AdminOrders"],
    }),
    getFeVersion: builder.query<string, void>({
      query: () => ({
        url: "/healthcheck/fe_version",
        method: "GET",
      }),
      providesTags: ["Healthcheck"],
      transformErrorResponse,
    }),
    getMexDolarDetails: builder.query<IMexDolarDetails, { order_id?: string }>({
      query: (params) => ({
        url: "/router/admin/orders/mexdollar_details",
        method: "GET",
        params,
      }),
      providesTags: (_, __, params) => [
        { type: "MexDolarOrderDetails", id: params?.order_id },
      ],
      transformErrorResponse,
    }),
    updateFeVersion: builder.mutation<string, { frontend_version: string }>({
      query: (body) => ({
        url: "/healthcheck/fe_version",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Healthcheck"],
      transformErrorResponse,
    }),
    approveOrderInvoice: builder.mutation<
      string,
      { order_router_id: string; invoice_accepted: boolean }
    >({
      query: (body) => ({
        url: "/router/admin/order/invoice_approval",
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, arg) => [
        "AdminOrders",
        { type: "RouterOrder", id: arg.order_router_id },
      ],
      transformErrorResponse,
    }),
    updateTradingDeskDetails: builder.mutation<
      string,
      {
        order_router_id: string;
        rate: number;
        ccy_source: string;
        ccy_dest: string;
        qty_source: number;
        qty_dest: number;
      }
    >({
      query: (body) => ({
        url: "/router/admin/orders/mexdollar/trading_desk_details",
        method: "POST",
        params: body,
      }),
      invalidatesTags: ["AdminOrders"],
      transformErrorResponse,
    }),
    createOrder: builder.mutation<{ id: string }, CreateOrderDTO>({
      query: (body) => ({
        url: "/router/admin/order",
        method: "POST",
        body,
      }),
      transformErrorResponse,
      invalidatesTags: ["AdminOrders"],
    }),
    getQuoteStream: builder.query<
      { quotes: Array<IQuoteStreamEntry>; cursor: string | null },
      IQuoteStreamQuery
    >({
      query: (params) => ({
        url: "/router/admin/quote_stream",
        method: "GET",
        params,
      }),
      transformErrorResponse,
    }),
    createTazaEntity: builder.mutation<
      string,
      { user_id: string; industry_vertical: string }
    >({
      query: (body) => ({
        url: "/router/admin/user/taza_entity",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AllClients"],
      transformErrorResponse,
    }),
  }),
});

export const {
  useCreateOrderMutation,
  useApproveOrderInvoiceMutation,
  useGetMexDolarDetailsQuery,
  useConfirmSPIDTransferMutation,
  useAddAdminTransferAccountsMutation,
  useGetAdminTransferAccountsQuery,
  useUserFlagsQuery,
  useUpdateUserMutation,
  useCreateUserOnlyAdminMutation,
  useModifyBrokerUsersMutation,
  useApproveOrderMutation,
  useCancelationLossMutation,
  useCreateBrokerAdminMutation,
  useCreateUserAdminMutation,
  useUpdateUserAdminMutation,
  useGetAllClientsQuery,
  useResendWelcomeEmailMutation,
  useConfirmOutboundTransferMutation,
  useDeleteDestinationAccountMutation,
  useUpdateDestinationAccountStatusMutation,
  useGetDestinationAccountsQuery,
  usePrefetch,
  useGetAdminOrdersQuery,
  useLazyGetAdminOrdersQuery,
  useGetLPDetailsQuery,
  useGetShinkansenBalanceQuery,
  usePostShinkansenTransferMutation,
  useGetAdminSummaryQuery,
  useRemoveRolloverMutation,
  useGetFeVersionQuery,
  useUpdateFeVersionMutation,
  useUpdateOrderStatusMutation,
  useUpdateTradingDeskDetailsMutation,
  useLazyGetQuoteStreamQuery,
  useCreateTazaEntityMutation,
} = adminApi;
